import { useEffect, useRef } from 'react';
import { track, EVENTS } from '../services/analytics';
import {
    LANDING_CTAS, isLandingSection, isLandingCta, isBuildTool,
} from '../content/landingIds';

// The landing page's text-free measurement (#2512). Two listeners on the
// page's root element, nothing per component:
//
// 1. SECTIONS. Every element carrying data-landing-section="<id>" is watched
//    by one IntersectionObserver. A section counts as seen when any part of
//    it crosses the middle band of the screen (rootMargin -35% top and
//    bottom), which works for a short band and for a feature row taller than
//    a phone screen alike. Each id fires ONCE per page view: the set lives in
//    a ref for the life of the page, so a re-render, StrictMode's double
//    mount, or a section that appears later (the free read once auth
//    resolves, the Build row once its flag does) never counts twice.
// 2. CLICKS. One capture-phase listener reads the id of the nearest
//    [data-cta] around the clicked link or button, and the key of the nearest
//    [data-build-tool] around any tap. Only ids in content/landingIds.js
//    reach track(); an element's text never does. A click that is not on a
//    link or button (a tap on a paragraph inside a CTA's container) is not a
//    CTA click.
//
// Everything goes through track(), so internal sessions and Do Not Track
// behave exactly as for every other event. No IntersectionObserver (an old
// browser, jsdom) means no section events, never an error.
export default function useLandingInstrumentation(rootRef, deps = []) {
    const seen = useRef(new Set());

    useEffect(() => {
        const root = rootRef.current;
        if (!root || typeof IntersectionObserver !== 'function') return undefined;
        // `order`: the section's place on the page as rendered, top to
        // bottom (1-based), read when the observer attaches.
        const order = new Map();
        const io = new IntersectionObserver((entries) => {
            for (const entry of entries) {
                if (!entry.isIntersecting) continue;
                const id = entry.target.getAttribute('data-landing-section');
                io.unobserve(entry.target);
                if (!isLandingSection(id) || seen.current.has(id)) continue;
                seen.current.add(id);
                track(EVENTS.LANDING_SECTION_SEEN, { section: id, order: order.get(entry.target) || 0 });
            }
        }, { rootMargin: '-35% 0px -35% 0px', threshold: 0 });
        root.querySelectorAll('[data-landing-section]').forEach((el, i) => {
            order.set(el, i + 1);
            if (!seen.current.has(el.getAttribute('data-landing-section'))) io.observe(el);
        });
        return () => io.disconnect();
        // `deps`: what makes a section mount after the first render (auth,
        // a flag), so the observer is re-attached and picks it up.
    }, [rootRef, ...deps]);

    useEffect(() => {
        const root = rootRef.current;
        if (!root) return undefined;
        const onClick = (ev) => {
            const el = ev.target;
            if (!el || typeof el.closest !== 'function') return;
            const tile = el.closest('[data-build-tool]');
            if (tile && root.contains(tile)) {
                const tool = tile.getAttribute('data-build-tool');
                if (isBuildTool(tool)) track(EVENTS.LANDING_BUILD_TILE_CLICKED, { tool });
            }
            const control = el.closest('a, button');
            if (!control || !root.contains(control)) return;
            const holder = control.closest('[data-cta]');
            const id = holder && holder.getAttribute('data-cta');
            if (!isLandingCta(id)) return;
            // The band the control sits in on the page as rendered, else the
            // table's (the fixed header): a CTA two orders place in different
            // bands (the build tiles, the tour link; #5128's story order
            // behind landing_story) carries where the visitor saw it.
            const band = control.closest('[data-landing-section]');
            const where = band && band.getAttribute('data-landing-section');
            track(EVENTS.LANDING_CTA_CLICKED, { cta_id: id, section: isLandingSection(where) ? where : LANDING_CTAS[id].section });
        };
        root.addEventListener('click', onClick, true);
        return () => root.removeEventListener('click', onClick, true);
    }, [rootRef]);
}
